import { useState, useMemo, useCallback, useEffect, useRef } from 'react';

/**
 * Client-side pagination hook.
 * Takes a complete dataset and returns paginated results with navigation controls.
 */
export const useClientSidePagination = (data = [], options = {}) => {
  const { initialPage = 0, initialRowsPerPage = 25 } = options;

  const [page, setPage] = useState(initialPage);
  const [rowsPerPage, setRowsPerPage] = useState(initialRowsPerPage);
  const prevDataLengthRef = useRef(data?.length || 0);

  const totalPages = useMemo(() => {
    return Math.ceil((data?.length || 0) / rowsPerPage);
  }, [data?.length, rowsPerPage]);

  useEffect(() => {
    const currentDataLength = data?.length || 0;
    const prevDataLength = prevDataLengthRef.current;
    if (currentDataLength !== prevDataLength) {
      const maxPage = Math.max(0, Math.ceil(currentDataLength / rowsPerPage) - 1);
      if (page > maxPage) {
        setPage(maxPage);
      } else if (currentDataLength > prevDataLength * 2 && prevDataLength > 0) {
        setPage(0);
      }
    }
    prevDataLengthRef.current = currentDataLength;
  }, [data?.length, page, rowsPerPage]);

  const paginatedData = useMemo(() => {
    const startIndex = page * rowsPerPage;
    return (data || []).slice(startIndex, startIndex + rowsPerPage);
  }, [data, page, rowsPerPage]);

  const handleChangePage = useCallback((event, newPage) => { setPage(newPage); }, []);
  const handleChangeRowsPerPage = useCallback((event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  }, []);
  const goToFirstPage = useCallback(() => { setPage(0); }, []);
  const goToLastPage = useCallback(() => { setPage(Math.max(0, totalPages - 1)); }, [totalPages]);
  const goToNextPage = useCallback(() => { setPage(prev => Math.min(prev + 1, totalPages - 1)); }, [totalPages]);
  const goToPreviousPage = useCallback(() => { setPage(prev => Math.max(prev - 1, 0)); }, []);
  const resetPage = useCallback(() => { setPage(0); }, []);

  return {
    paginatedData,
    page, rowsPerPage, totalPages,
    totalCount: data?.length || 0,
    startIndex: page * rowsPerPage,
    endIndex: Math.min((page + 1) * rowsPerPage, data?.length || 0),
    handleChangePage, handleChangeRowsPerPage,
    goToFirstPage, goToLastPage, goToNextPage, goToPreviousPage, resetPage,
    setPage, setRowsPerPage,
    isFirstPage: page === 0,
    isLastPage: page >= totalPages - 1,
    hasNextPage: page < totalPages - 1,
    hasPreviousPage: page > 0
  };
};

export default useClientSidePagination;
